import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { MeetingStatusTagComponent } from '@features/dashboard/meetings/components/meeting-status-tag/meeting-status-tag.component';
import {
  iniciaisDe,
  linhaDeApoio,
  organizacoesDaLigacao,
  rotuloDoDealVinculado,
  rotuloDoTipoDaCall,
  situacaoDaProxima,
  tomDoTipoDaCall,
  temBotAtivo,
} from '@features/dashboard/meetings/components/meeting-presenter';
import {
  MeetingLifecycleAction,
  meetingLifecycleOptions,
} from '@features/dashboard/meetings/components/meeting-lifecycle-actions';
import { AbaDaTela } from '@features/dashboard/meetings/pages/meetings/meetings-url-state';
import {
  displayTitle,
  originalTitleIfDifferent,
  renamedByText,
} from '@features/dashboard/meetings/components/meeting-visibility-presenter';
import { MeetingVisibilityIconsComponent } from '@features/dashboard/meetings/components/meeting-visibility-icons/meeting-visibility-icons.component';

/** Um chip de organização participante (empresa, parceiro ou banco). */
interface ChipDeOrganizacao {
  nome: string;
  icone: string;
}

/** Situações de reunião acontecendo agora: a linha fica destacada (card 3162). */
const SITUACOES_AO_VIVO: readonly string[] = ['Joining', 'InWaitingRoom', 'Recording'];

/** Quantos chips cabem na linha; o resto vira "+N". */
const CHIPS_NA_LINHA = 2;

/** Pedido de ligar ou desligar o bot de uma reunião da aba Próximas. */
export interface AlternarBot {
  meeting: MeetingDto;
  ligar: boolean;
}

/** Pedido de arquivar, restaurar ou excluir uma reunião (card 3786); quem confirma é a tela. */
export interface MeetingLifecycleRequest {
  meeting: MeetingDto;
  action: MeetingLifecycleAction;
}

/**
 * Uma reunião da lista em cards da tela Meetings (card 3162, design `Meetings.dc.html`): iniciais do
 * organizador, título, "data · hora · duração · organizador", organizações participantes, deal (ou
 * "Vincular deal"), situação e o menu da linha. Na aba Próximas, troca a duração pelo toggle "Bot" e
 * a situação por "Agendado / Com bot" ou "Sem bot".
 *
 * Só apresenta e avisa: quem chama a API é a tela. Clicar na linha abre o detalhe; o link, o toggle
 * e o menu não abrem (`stopPropagation`).
 */
@Component({
  selector: 'app-meeting-row',
  standalone: true,
  imports: [SharedModule, MeetingStatusTagComponent, MeetingVisibilityIconsComponent],
  templateUrl: './meeting-row.component.html',
  styleUrl: './meeting-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingRowComponent {
  readonly meeting = input.required<MeetingDto>();
  readonly aba = input<AbaDaTela>('recentes');
  /** Uma ação desta linha está em andamento: toggle e menu ficam travados. */
  readonly ocupada = input(false);
  /** `MEETING_ARCHIVE` de quem está logado: Arquivar / Restaurar no menu (card 3786). */
  readonly canArchive = input(false);
  /** `MEETING_DELETE` de quem está logado; Excluir ainda exige `canDelete` da reunião (card 3786). */
  readonly canDelete = input(false);
  /** Primeira linha do período: mostra o rótulo "Quem vê" acima dos ícones (card 3803). */
  readonly showVisibilityLabel = input(false);

  readonly abrir = output<MeetingDto>();
  readonly vincular = output<MeetingDto>();
  readonly desvincular = output<MeetingDto>();
  readonly alternarAcesso = output<MeetingDto>();
  readonly alternarBot = output<AlternarBot>();
  readonly lifecycleAction = output<MeetingLifecycleRequest>();

  /** Arquivada (card 3786): só aparece na aba Arquivadas. */
  protected readonly archivedOn = computed(() => {
    const archivedAt = this.meeting().archivedAt;
    return archivedAt ? this.datas.formatDateTimeInFormat(archivedAt, 'dd/MM/yyyy') : null;
  });

  protected readonly ehProximas = computed(() => this.aba() === 'proximas');
  protected readonly aoVivo = computed(() => SITUACOES_AO_VIVO.includes(this.meeting().status));

  protected readonly iniciais = computed(() =>
    iniciaisDe(this.meeting().organizerName || this.meeting().organizerEmail)
  );

  /** "30/09 · 19:00" quando a reunião foi remarcada no calendário (card 3191). */
  protected readonly remarcadaPara = computed(() => {
    const m = this.meeting();
    return m.status === 'Rescheduled' && m.rescheduledToStartAt
      ? this.datas.formatDateTimeInFormat(m.rescheduledToStartAt, "dd/MM '·' HH:mm")
      : null;
  });

  /** Tipo da reunião classificado pelo Cog, quando existe (02/10/2026). */
  protected readonly tipo = computed(() => rotuloDoTipoDaCall(this.meeting().callType));
  protected readonly tomDoTipo = computed(() => tomDoTipoDaCall(this.meeting().callType));

  protected readonly apoio = computed(() => {
    const m = this.meeting();
    const diaEHora = this.datas.formatDateTimeInFormat(m.scheduledStartAt, "dd/MM '·' HH:mm");
    return linhaDeApoio(diaEHora, m, !this.ehProximas());
  });

  // ─────────────── título e visibilidade (card 3803) ───────────────

  /** Título exibido (o backend já escolhe: o da pessoa, o sugerido pelo Cog ou o do convite). */
  protected readonly title = computed(() => displayTitle(this.meeting()));

  /** Linha de apoio com o título do convite no fim, quando ele difere do exibido. */
  protected readonly supportLine = computed(() => {
    const original = originalTitleIfDifferent(this.meeting());
    return original ? `${this.apoio()} · Original: “${original}”` : this.apoio();
  });

  /** "Renomeada por X em DD/MM" no lápis ao lado do título; nulo quando o título é automático. */
  protected readonly renamedBy = computed(() => {
    const m = this.meeting();
    const on = m.customTitleAt ? this.datas.formatDateTimeInFormat(m.customTitleAt, 'dd/MM') : null;
    return renamedByText(m, on);
  });

  /** Selo "Privada" (Privar ligado): o `title` diz quem privou. Nulo quando não está privada. */
  protected readonly privateTooltip = computed(() => {
    const visibility = this.meeting().visibility;
    if (!visibility?.isPrivate) return null;
    return visibility.privateByName
      ? `Privada por ${visibility.privateByName}: só quem participou vê`
      : 'Privada: só quem participou vê';
  });

  protected readonly chips = computed<ChipDeOrganizacao[]>(() => {
    const m = this.meeting();
    return [
      ...organizacoesDaLigacao(m, 'Company').map(nome => ({ nome, icone: 'pi pi-building' })),
      ...organizacoesDaLigacao(m, 'Partner').map(nome => ({ nome, icone: 'pi pi-briefcase' })),
      ...organizacoesDaLigacao(m, 'Bank').map(nome => ({ nome, icone: 'pi pi-building-columns' })),
    ];
  });
  protected readonly chipsVisiveis = computed(() => this.chips().slice(0, CHIPS_NA_LINHA));
  protected readonly chipsOcultos = computed(() => this.chips().slice(CHIPS_NA_LINHA));
  protected readonly nomesOcultos = computed(() =>
    this.chipsOcultos()
      .map(c => c.nome)
      .join(', ')
  );

  protected readonly deal = computed(() => this.meeting().deals?.[0] ?? null);
  protected readonly outrosDeals = computed(() =>
    Math.max(0, (this.meeting().deals?.length ?? 0) - 1)
  );
  protected readonly rotuloDoDeal = rotuloDoDealVinculado;

  protected readonly botLigado = computed(() => temBotAtivo(this.meeting()));
  /** Dá para mexer no toggle: desligar um bot cancelável ou ligar um cancelado que ainda não começou. */
  protected readonly botAlteravel = computed(() => {
    const m = this.meeting();
    return this.botLigado() ? m.canCancelBot : m.canScheduleBot;
  });
  protected readonly situacaoProxima = computed(() => situacaoDaProxima(this.meeting()));

  protected readonly acoes = computed<MenuItem[]>(() => {
    const m = this.meeting();
    const itens: MenuItem[] = [
      { label: 'Vincular', icon: 'pi pi-link', command: () => this.vincular.emit(m) },
    ];
    if (m.deals?.length) {
      itens.push({
        label: 'Desvincular',
        icon: 'pi pi-times-circle',
        command: () => this.desvincular.emit(m),
      });
    }
    if (m.canManageAccess) {
      itens.push(
        m.isShared
          ? {
              label: 'Voltar a privada',
              icon: 'pi pi-lock',
              command: () => this.alternarAcesso.emit(m),
            }
          : {
              label: 'Disponibilizar para acesso geral',
              icon: 'pi pi-globe',
              command: () => this.alternarAcesso.emit(m),
            }
      );
    }
    const lifecycle = meetingLifecycleOptions(m, {
      canArchive: this.canArchive(),
      canDelete: this.canDelete(),
    });
    if (lifecycle.length) {
      itens.push({ separator: true });
      lifecycle.forEach(option =>
        itens.push({
          label: option.label,
          icon: option.icon,
          command: () => this.lifecycleAction.emit({ meeting: m, action: option.action }),
        })
      );
    }
    return itens;
  });

  /**
   * O `app-switch` guarda o próprio estado (`ngModel`): clicado, ele vira sozinho, mesmo que o usuário
   * desista na confirmação ou a API recuse. Trocar a versão recria o switch com o estado real
   * (`botLigado`); quando a API confirma, a reunião nova chega e o switch acompanha.
   */
  protected readonly versaoDoToggle = signal(0);

  constructor(private readonly datas: DateFormatterService) {}

  protected aoAlternarBot(ligar: boolean): void {
    this.versaoDoToggle.update(v => v + 1);
    this.alternarBot.emit({ meeting: this.meeting(), ligar });
  }
}
